import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AppBarMenus, Categories, FooterCategories } from '../constants/menu-items';
import { signedUploadUrl } from '../api/employeePortal';
import axios from 'axios';
import { AuthData, ManualItem } from '../types';

type ManualType = 'pdf' | 'html-zip';

interface ManualFormProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  items: ManualItem[];
  onSubmit: (data: ManualItem, mode: 'add' | 'edit') => Promise<void>;
  getPresignedUrl: (fileInfo: { name: string; type: string }) => Promise<{
    presigned_url: string;
    fields: Record<string, string>;
    public_url: string;
  }>;
  uploadFileToS3: (url: string, fields: Record<string, string>, file: File) => Promise<void>;
}

const ManualFormDialog: React.FC<ManualFormProps> = ({ 
  open, 
  onClose, 
  mode, 
  items, 
  onSubmit,
  getPresignedUrl,
  uploadFileToS3,
}) => {
  const [formData, setFormData] = useState<Omit<ManualItem, 'manualId' | 'createdAt' | 'updatedAt'>>({
    name: '',
    type: 'file',
    fileType: 'pdf',
    fileUrl: '',
    externalUrl: '',
    locations: {},
    isActive: true
  });

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // const [fileName, setFileName] = useState('');
  const [manualType, setManualType] = useState<ManualType>('pdf');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);

  useEffect(() => {
    if (mode === 'edit' && selectedItemId) {
      const itemToEdit = items.find(item => item.manualId === selectedItemId);
      if (itemToEdit) {
        setFormData({
          name: itemToEdit.name,
          type: itemToEdit.type,
          fileType: itemToEdit.fileType,
          fileUrl: itemToEdit.fileUrl || '',
          externalUrl: itemToEdit.externalUrl || '',
          locations: { ...itemToEdit.locations },
          isActive: itemToEdit.isActive
        });
        // setFileName(itemToEdit.fileUrl ? 'Archivo actual: ' + itemToEdit.fileUrl.split('/').pop() : '');
      }
    } else {
      // Reset form for add mode
      setFormData({
        name: '',
        type: 'file',
        fileType: 'pdf',
        fileUrl: '',
        externalUrl: '',
        locations: {},
        isActive: true
      });
      // setSelectedFile(null);
      // setFileName('');
    }
  }, [mode, selectedItemId, items]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (e.target.files && e.target.files.length > 0) {
  //     const file = e.target.files[0];
  //     setSelectedFile(file);
  //     setFileName(file.name);
  //   }
  // };

  const handleLocationChange = (locationType: 'appBar' | 'documents' | 'footer', key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [locationType]: {
          ...prev.locations[locationType],
          [key]: value
        }
      }
    }));
  };

  const handleToggleLocation = (locationType: 'appBar' | 'documents' | 'footer', show: boolean) => {
    if (!show) {
      const newLocations = { ...formData.locations };
      delete newLocations[locationType];
      setFormData(prev => ({
        ...prev,
        locations: newLocations
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        locations: {
          ...prev.locations,
          [locationType]: locationType === 'appBar' ? { item: '' } : 
                         locationType === 'documents' ? { category: '' } : 
                         { section: '' }
        }
      }));
    }
  };

  const handleFileTypeChange = (type: ManualType) => {
    setManualType(type);
    setPdfFile(null);
    setZipFile(null);
  };
  
  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setPdfFile(e.target.files[0]);
    }
  };
  
  const handleZipUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setZipFile(e.target.files[0]);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      let fileUrl = formData.fileUrl;
      
      if (manualType === 'pdf' && pdfFile) {
        const { presigned_url, fields, public_url } = await getPresignedUrl({
          name: pdfFile.name,
          type: pdfFile.type
        });
        await uploadFileToS3(presigned_url, fields, pdfFile);
        fileUrl = public_url;
      } 
      else if (manualType === 'html-zip' && zipFile) {
        const { presigned_url, fields, public_url } = await getPresignedUrl({
          name: zipFile.name,
          type: 'application/zip'
        });
        await uploadFileToS3(presigned_url, fields, zipFile);
        fileUrl = public_url;
      }
  
      await onSubmit({
        ...formData,
        fileUrl,
        fileType: manualType
      }, mode);
      
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'Agregar Nuevo Item' : 'Modificar Item Existente'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {mode === 'edit' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Seleccionar Item a Modificar</InputLabel>
              <Select
                value={selectedItemId || ''}
                onChange={(e) => setSelectedItemId(e.target.value as string)}
                required
              >
                {items.map(item => (
                  <MenuItem key={item.manualId} value={item.manualId}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                      <span>{item.name}</span>
                      <Chip 
                        label={item.isActive ? 'Activo' : 'Inactivo'} 
                        size="small"
                        color={item.isActive ? 'success' : 'error'}
                        icon={item.isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Nombre del item"
            name="name"
            disabled={mode === 'edit'}
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo</InputLabel>
            <Select
              name="type"
              value={formData.type}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                type: e.target.value as 'file' | 'link',
                fileUrl: e.target.value === 'link' ? '' : prev.fileUrl,
                externalUrl: e.target.value === 'file' ? '' : prev.externalUrl
              }))}
              required
            >
              <MenuItem value="file">Archivo</MenuItem>
              <MenuItem value="link">Enlace</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Manual</InputLabel>
            <Select
              value={manualType}
              onChange={(e) => handleFileTypeChange(e.target.value as ManualType)}
              required
            >
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="html-zip">HTML (ZIP con assets)</MenuItem>
            </Select>
          </FormControl>

          {formData.type === 'file' ? (
            manualType === 'pdf' ? (
              <>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfUpload}
                  style={{ display: 'none' }}
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    {pdfFile ? pdfFile.name : 'Subir archivo PDF'}
                  </Button>
                </label>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleZipUpload}
                  style={{ display: 'none' }}
                  id="zip-upload"
                />
                <label htmlFor="zip-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 1 }}
                  >
                    {zipFile ? zipFile.name : 'Subir ZIP con HTML y assets'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  El ZIP debe contener el archivo HTML principal y todos los assets (CSS, JS, imágenes) con rutas relativas
                </Typography>
              </>
            )
          ) : (
            <TextField
              fullWidth
              label="URL del enlace"
              name="externalUrl"
              value={formData.externalUrl}
              onChange={handleChange}
              margin="normal"
              required
            />
          )}

          {/* Resto del formulario (ubicaciones, estado activo) se mantiene igual */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Ubicación</Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.locations.appBar}
                  onChange={(e) => handleToggleLocation('appBar', e.target.checked)}
                />
              }
              label="Mostrar en AppBar"
            />

            {formData.locations.appBar && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Item principal</InputLabel>
                  <Select
                    value={formData.locations.appBar.item}
                    onChange={(e) => handleLocationChange('appBar', 'item', e.target.value)}
                    required
                  >
                    {AppBarMenus
                        .filter(i => i.children)
                        .map(i => 
                            <MenuItem 
                                key={i.label} 
                                value={i.label}
                            >
                                {i.label}
                            </MenuItem>
                        )}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Sub-item (opcional)"
                  value={formData.locations.appBar.subItem || ''}
                  onChange={(e) => handleLocationChange('appBar', 'subItem', e.target.value)}
                  margin="normal"
                />
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.locations.documents}
                  onChange={(e) => handleToggleLocation('documents', e.target.checked)}
                />
              }
              label="Mostrar en Documentos y Guías"
            />

            {formData.locations.documents && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={formData.locations.documents.category}
                    onChange={(e) => handleLocationChange('documents', 'category', e.target.value)}
                    required
                  >
                    {Categories.map(({title}) => (
                      <MenuItem key={title} value={title}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.locations.footer}
                  onChange={(e) => handleToggleLocation('footer', e.target.checked)}
                />
              }
              label="Mostrar en Footer"
            />

            {formData.locations.footer && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Sección</InputLabel>
                  <Select
                    value={formData.locations.footer.section}
                    onChange={(e) => handleLocationChange('footer', 'section', e.target.value)}
                    required
                  >
                    {FooterCategories.map(i => (
                        <MenuItem 
                            key={i.title} 
                            value={i.title}
                        >
                            {i.title}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
            }
            label="Item activo (visible en la aplicación)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={isSubmitting || (formData.type === 'file' && !pdfFile && !zipFile) || (formData.type === 'link' && !formData.fileUrl)}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : mode === 'add' ? 'Agregar' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const AdminManualButton: React.FC<{ 
  items: ManualItem[]; 
  onAddOrEdit: (data: any, mode: 'add' | 'edit') => Promise<void>;
  userIsAdmin: AuthData;
  onCloseMenu: () => void;
}> = ({ 
  items, 
  onAddOrEdit,
  userIsAdmin,
  onCloseMenu,
}) => {
  const [openMode, setOpenMode] = useState<'add' | 'edit' | null>(null);

  const handleOpen = (mode: 'add' | 'edit') => {
    setOpenMode(mode);
  };

  const handleClose = () => {
    setOpenMode(null);
  };

  const getPresignedUrl = async (fileinfo: { name: string; type: string; }) => {
    const response = await signedUploadUrl(
      `${userIsAdmin.UserId}`, 
      {file_type: fileinfo.type}
    );
    // const data = await response.json();
    return response;
  };
  
  const uploadFileToS3 = async (url: string, fields: Record<string, string>, file: File) => {
    // const response = await fetch(url, {
    //   method: 'PUT',
    //   body: file,
    //   headers: {
    //     'Content-Type': file.type
    //   }
    // });
    
    // if (!response.ok) {
    //   throw new Error('Error al subir el archivo');
    // }

    const formData = new FormData();
    const presignedData = fields as any
    for (let key in presignedData) {
      const value = presignedData[key];
      formData.append(key, value);
      }
    formData.append('file', file);
    const response = await axios.post(url, formData);
    if (response.status >= 200 && response.status < 300) {
      // return true;
    } else {
      console.error('Failed to upload file:');
      console.log(response);
      // return false;
    }
  };

  return (
    <>
      
        <Box 
            sx={{ 
                position: 'absolute', 
                bottom: '100%', 
                right: 0,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
            }}
        >
            <Button
                variant="contained"
                // color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpen('add')}
                sx={{
                    whiteSpace: 'nowrap',
                    justifyContent: 'flex-start'
                }}
            >
                Agregar Manual
            </Button>
            <Button
                variant="contained"
                // color="secondary"
                startIcon={<EditIcon />}
                onClick={() => handleOpen('edit')}
                disabled={items.length === 0}
                sx={{
                    whiteSpace: 'nowrap',
                    justifyContent: 'flex-start'
                }}
            >
                Modificar Manual
            </Button>
        </Box>

        {openMode && (
            <ManualFormDialog
            open={!!openMode}
            onClose={handleClose}
            mode={openMode}
            items={items}
            onSubmit={onAddOrEdit}
            getPresignedUrl={getPresignedUrl}
            uploadFileToS3={uploadFileToS3}
            />
        )}
    </>
  );
};

export default AdminManualButton;